import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { useHistory } from 'react-router-dom';
import { Row, Col, Typography, message } from 'antd';
import { DashboardContainer } from 'components/Container/Container';
import { LoadingSpinnerView } from 'components/LoadingSpinner/LoadingSpinner';
import Api from 'api';

// Dashboard Components
import StatsCard from 'components/Dashboard/StatsCard';
import LevelProgress from 'components/Dashboard/LevelProgress';
import StreakTracker from 'components/Dashboard/StreakTracker';
import AchievementBadge from 'components/Dashboard/AchievementBadge';
import SpeciesTreemap from 'components/Dashboard/SpeciesTreemap';
import Leaderboard from 'components/Dashboard/Leaderboard';
import ActivityFeed from 'components/Dashboard/ActivityFeed';
import RareSpeciesCard from 'components/Dashboard/RareSpeciesCard';
import ErrorCorrectionLeaderboard from 'components/Dashboard/ErrorCorrectionLeaderboard';
import { useUser } from 'helper/UserContext';

const { Title } = Typography;

const WelcomeHeader = styled.div`
  margin-bottom: 32px;

  h1 {
    margin-bottom: 8px;
  }
  .subtitle {
    color: #8c8c8c;
    font-size: 16px;
  }
`;

const DashboardGrid = styled.div`
  .stats-row {
    margin-bottom: 24px;
  }
  .main-content-row {
    margin-bottom: 24px;
  }
`;

interface Props {}

const Dashboard: React.FC<Props> = () => {
  const { user } = useUser();
  const history = useHistory();
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState<any>(null);
  const [achievements, setAchievements] = useState<any[]>([]);
  const [leaderboard, setLeaderboard] = useState<any>({ leaderboard: [], current_user_rank: 0 });
  const [leaderboardScope, setLeaderboardScope] = useState<'global' | 'regional'>('global');
  const [activities, setActivities] = useState<any[]>([]);
  const [treemapData, setTreemapData] = useState<any>({ name: 'Species', children: [] });
  const [rareSpecies, setRareSpecies] = useState<any[]>([]);
  const [commonSpecies, setCommonSpecies] = useState<any[]>([]);
  const [errorLeaderboard, setErrorLeaderboard] = useState<any>({ leaderboard: [], current_user: { rank: 0, corrections_count: 0, reports_submitted: 0, accuracy: 0 } });

  useEffect(() => {
    fetchDashboardData();
  }, []);

  useEffect(() => {
    fetchLeaderboard(leaderboardScope);
  }, [leaderboardScope]);

  const fetchDashboardData = async () => {
    try {
      setLoading(true);

      // Fetch all dashboard data in parallel
      const [
        statsRes,
        achievementsRes,
        leaderboardRes,
        activitiesRes,
        treemapRes,
        rareRes,
        commonRes,
        errorLeaderboardRes
      ] = await Promise.all([
        Api.get('/dashboard/stats'),
        Api.get('/dashboard/achievements'),
        Api.get(`/dashboard/leaderboard?scope=global&limit=50`),
        Api.get('/dashboard/activity-feed?limit=20'),
        Api.get('/dashboard/treemap-data?type=all'),
        Api.get('/dashboard/rare-species?limit=5'),
        Api.get('/dashboard/common-species?limit=5'),
        Api.get('/species-errors/leaderboard?limit=50')
      ]);

      setStats(statsRes.data.data);
      setAchievements(achievementsRes.data.data.achievements || []);
      setLeaderboard({
        leaderboard: leaderboardRes.data.data.leaderboard || [],
        current_user_rank: leaderboardRes.data.data.current_user_rank || 0
      });
      setActivities(activitiesRes.data.data.activities || []);
      setTreemapData(treemapRes.data.data || { name: 'Species', children: [] });
      setRareSpecies(rareRes.data.data || []);
      setCommonSpecies(commonRes.data.data || []);
      setErrorLeaderboard({
        leaderboard: errorLeaderboardRes.data.data.leaderboard || [],
        current_user: errorLeaderboardRes.data.data.current_user || { rank: 0, corrections_count: 0, reports_submitted: 0, accuracy: 0 }
      });

      setLoading(false);
    } catch (error: any) {
      console.error('Error fetching dashboard data:', error);
      message.error('Failed to load dashboard data');
      setLoading(false);
    }
  };

  const fetchLeaderboard = async (scope: 'global' | 'regional') => {
    try {
      const { data } = await Api.get(`/dashboard/leaderboard?scope=${scope}&limit=50`);
      setLeaderboard({
        leaderboard: data.data.leaderboard || [],
        current_user_rank: data.data.current_user_rank || 0
      });
    } catch (error) {
      console.error('Error fetching leaderboard:', error);
    }
  };

  const handleLeaderboardScopeChange = (scope: 'global' | 'regional') => {
    setLeaderboardScope(scope);
  };

  if (loading || !stats) {
    return <LoadingSpinnerView size={80} />;
  }

  const nextLevelPoints = stats.level * 500; // 500 points per level

  return (
    <DashboardContainer>
      <WelcomeHeader>
        <Title level={1}>Welcome back, {user?.name || 'Naturalist'}! 🌿</Title>
        <div className="subtitle">
          Here's your biodiversity tracking progress
        </div>
      </WelcomeHeader>

      <DashboardGrid>
        {/* Top Stats Row */}
        <Row gutter={[16, 16]} className="stats-row">
          <Col xs={24} sm={12} lg={6}>
            <StatsCard
              title="Total Species"
              value={stats.total_species || 0}
              prefix="🌍"
            />
          </Col>
          <Col xs={24} sm={12} lg={6}>
            <StatsCard
              title="This Week"
              value={stats.this_week_additions || 0}
              prefix="📈"
              trend={stats.this_week_additions > 0 ? `+${stats.this_week_additions} new` : 'No additions'}
              trendPositive={stats.this_week_additions > 0}
            />
          </Col>
          <Col xs={24} sm={12} lg={6}>
            <StatsCard
              title="Collections"
              value={stats.total_collections || 0}
              prefix="📚"
              onClick={() => history.push('/collections/plants')}
            />
          </Col>
          <Col xs={24} sm={12} lg={6}>
            <StatsCard
              title="Current Streak"
              value={stats.current_streak || 0}
              suffix="days"
              prefix="🔥"
            />
          </Col>
        </Row>

        {/* Level & Streak Row */}
        <Row gutter={[16, 16]} className="main-content-row">
          <Col xs={24} lg={16}>
            <LevelProgress
              level={stats.level || 1}
              points={stats.points || 0}
              nextLevelPoints={nextLevelPoints}
            />
          </Col>
          <Col xs={24} lg={8}>
            <StreakTracker
              currentStreak={stats.current_streak || 0}
              longestStreak={stats.longest_streak || 0}
            />
          </Col>
        </Row>

        {/* Treemap & Achievements Row */}
        <Row gutter={[16, 16]} className="main-content-row">
          <Col xs={24} lg={16}>
            <SpeciesTreemap data={treemapData} />
          </Col>
          <Col xs={24} lg={8}>
            <AchievementBadge achievements={achievements} maxDisplay={6} />
          </Col>
        </Row>

        {/* Rare & Common Species Row */}
        <Row gutter={[16, 16]} className="main-content-row">
          <Col xs={24} lg={12}>
            <RareSpeciesCard species={rareSpecies} type="rare" />
          </Col>
          <Col xs={24} lg={12}>
            <RareSpeciesCard species={commonSpecies} type="common" />
          </Col>
        </Row>

        {/* Leaderboard & Activity Feed Row */}
        <Row gutter={[16, 16]} className="main-content-row">
          <Col xs={24} lg={14}>
            <Leaderboard
              leaderboard={leaderboard.leaderboard}
              currentUserRank={leaderboard.current_user_rank}
              scope={leaderboardScope}
              onScopeChange={handleLeaderboardScopeChange}
            />
          </Col>
          <Col xs={24} lg={10}>
            <ActivityFeed activities={activities} />
          </Col>
        </Row>

        {/* Error Correction Leaderboard Row */}
        <Row gutter={[16, 16]} className="main-content-row">
          <Col xs={24}>
            <ErrorCorrectionLeaderboard
              leaderboard={errorLeaderboard.leaderboard}
              currentUser={errorLeaderboard.current_user}
            />
          </Col>
        </Row>
      </DashboardGrid>
    </DashboardContainer>
  );
};

export default Dashboard;
